/**
 * How the ceremony's return frame tells the signing page what happened.
 *
 * DocuSign ends a ceremony by navigating its iframe to our configured return
 * URL. That frame is a page of ours, same-origin with the signing page that
 * frames it, and the only thing it knows that the parent does not is the
 * outcome DocuSign put in the query string. So it forwards exactly that and
 * decides nothing else.
 *
 * The parent is the right place for every decision that follows. It never left
 * the tab, so it still holds the session, the order id, and the completion UI —
 * whereas the return frame is a fresh document that would have to rebuild all
 * three from scratch, inside an iframe, to answer questions it has no business
 * asking.
 *
 * This duplicates DocuSign's own end-of-ceremony message rather than replacing
 * it. That one depends on our origin being configured in the recipient view's
 * ``messageOrigins`` and on DocuSign actually sending it; this one only depends
 * on the redirect we configured. Either is enough, and the signing page ignores
 * whichever arrives second.
 */

/** Marks a message as ours, so the signing page can tell it from DocuSign's. */
export const SIGN_RETURN_MESSAGE_SOURCE = 'inflection.sign.return'

/** DocuSign's ``event`` value for a ceremony that produced a signature. */
export const SIGNING_COMPLETE_EVENT = 'signing_complete'

/**
 * DocuSign ``event`` values that mean the signer did not complete. Anything
 * unrecognised is deliberately absent: a return reached without a usable event
 * is not evidence of either outcome.
 */
const CANCELLED_EVENTS: ReadonlySet<string> = new Set(['cancel', 'decline', 'session_timeout', 'ttl_expired'])

/**
 * What the ceremony ended as.
 *
 * ``'unknown'`` is its own outcome rather than a failure. The event is a query
 * parameter, so it can be absent or unrecognised, and claiming a signature was
 * captured on that evidence would be the one wrong answer. The signing page
 * treats it as "over, but unverified" and says so.
 */
export type SignReturnOutcome = 'complete' | 'cancelled' | 'unknown'

/** The outcome DocuSign's ``event`` parameter describes. */
export const signReturnOutcomeFromEvent = (event: string | null | undefined): SignReturnOutcome => {
  const token = event?.trim().toLowerCase() ?? ''
  if (token === SIGNING_COMPLETE_EVENT) return 'complete'
  return CANCELLED_EVENTS.has(token) ? 'cancelled' : 'unknown'
}

/** What the return frame posts up to the signing page. */
export interface SignReturnMessage {
  source: typeof SIGN_RETURN_MESSAGE_SOURCE
  outcome: SignReturnOutcome
  /** The order DocuSign handed back, or ``''`` when it named none. */
  orderId: string
}

const OUTCOMES: ReadonlySet<string> = new Set<SignReturnOutcome>(['complete', 'cancelled', 'unknown'])

/**
 * The outcome carried by a ``message`` event from our own return frame, or
 * ``null`` when the event is not one.
 *
 * Only this exact origin is trusted. The signing page runs under a relaxed
 * CSP/COEP so it can frame a cross-origin ceremony, which means it is listening
 * on a window that genuinely cross-origin content is posting to; anything that
 * is not literally us is somebody else.
 */
export const signReturnMessageFrom = (origin: string, data: unknown): SignReturnMessage | null => {
  if (origin !== window.location.origin) return null
  if (data == null || typeof data !== 'object') return null
  const message = data as Record<string, unknown>
  if (message.source !== SIGN_RETURN_MESSAGE_SOURCE) return null
  if (typeof message.outcome !== 'string' || !OUTCOMES.has(message.outcome)) return null
  return {
    source: SIGN_RETURN_MESSAGE_SOURCE,
    outcome: message.outcome as SignReturnOutcome,
    orderId: typeof message.orderId === 'string' ? message.orderId : '',
  }
}
